import React, { useState, useEffect } from 'react';
import { Loader2, Clock, Play, Pause } from 'lucide-react';
import { AudioPlayer } from './AudioPlayer';
import { getFtpStreamUrl } from '@/services/api';
import { cn } from '@/lib/utils';

export type PreviewItemType = 'intro' | 'outro' | 'commercial';

interface MergePreviewItemProps {
  type: PreviewItemType;
  label: string;
  audioPath: string;
  companyId?: string; // For cross-company FTP access
  index?: number;
  duration?: number | null; // Duration from API (preferred)
  isPlaying?: boolean;
  isActiveInPlayAll?: boolean; // True when this item is the current one in "Play All" mode
  isPlayAllPaused?: boolean; // True when "Play All" is paused
  onPlayStateChange?: (id: string, playing: boolean) => void;
  onEnded?: () => void;
  onDurationLoaded?: (duration: number) => void;
}

/**
 * Format duration in seconds to MM:SS
 */
function formatDuration(seconds: number | null): string {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get styling based on item type
 * Uses Tailwind classes that work with the theme
 */
function getTypeConfig(type: PreviewItemType) {
  switch (type) {
    case 'intro':
    case 'outro':
      return {
        // Muted background to distinguish from regular clips
        className: 'border-border bg-muted/50',
        buttonClassName: 'bg-primary text-primary-foreground hover:bg-primary/90',
      };
    case 'commercial':
      return {
        // Same muted styling for commercials
        className: 'border-border bg-muted/50',
        buttonClassName: 'bg-primary text-primary-foreground hover:bg-primary/90',
      };
  }
}

/**
 * Card component for intro/outro/commercial segments in the merge preview.
 * Simpler than ClipCard - just shows label, duration, and audio player.
 */
export const MergePreviewItem: React.FC<MergePreviewItemProps> = ({
  type,
  label,
  audioPath,
  companyId,
  index,
  duration: durationProp,
  isPlaying: isPlayingProp = false,
  isActiveInPlayAll = false,
  isPlayAllPaused = false,
  onPlayStateChange,
  onEnded,
  onDurationLoaded,
}) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(true);
  // Use duration from prop (API) if available, otherwise fall back to local state
  const [localDuration, setLocalDuration] = useState<number | null>(null);
  const duration = durationProp ?? localDuration;

  const config = getTypeConfig(type);

  // Generate a unique ID for this item
  const itemId = `${type}-${index ?? 0}`;

  // Load audio URL on mount
  useEffect(() => {
    if (!audioPath) {
      setAudioError('No audio path configured');
      setIsLoadingUrl(false);
      return;
    }

    setIsLoadingUrl(true);
    getFtpStreamUrl(audioPath, companyId)
      .then((url) => {
        setAudioUrl(url);
        setIsLoadingUrl(false);
      })
      .catch((err) => {
        console.error('Failed to get audio URL:', err);
        setAudioError('Failed to load audio');
        setIsLoadingUrl(false);
      });
  }, [audioPath, companyId]);

  // Notify parent when duration is available from prop
  useEffect(() => {
    if (durationProp != null && durationProp > 0) {
      onDurationLoaded?.(durationProp);
    }
  }, [durationProp, onDurationLoaded]);

  // Handle duration loaded from AudioPlayer (fallback if API didn't provide it)
  const handleDurationLoaded = (dur: number) => {
    if (durationProp == null) {
      setLocalDuration(dur);
      onDurationLoaded?.(dur);
    }
  };

  const handlePlayToggle = () => {
    if (isPlayingProp) {
      onPlayStateChange?.(itemId, false);
    } else {
      onPlayStateChange?.(itemId, true);
    }
  };

  const handleAudioEnded = () => {
    onPlayStateChange?.(itemId, false);
    onEnded?.();
  };

  // Display label with index if provided (e.g., "Commercial 1")
  const displayLabel = index !== undefined ? `${label} ${index}` : label;

  const canPlay = audioUrl && !audioError && !isLoadingUrl;

  return (
    <div
      className={cn(
        "border rounded-lg overflow-hidden transition-all duration-300",
        config.className,
        (isPlayingProp || isActiveInPlayAll) && "border-cyan-500"
      )}
      style={{ backgroundColor: (isPlayingProp || isActiveInPlayAll) ? 'rgba(6, 182, 212, 0.1)' : 'var(--pm-bg-card)' }}
    >
      <div className="flex items-center gap-3 p-4">
        {/* Play/Pause button */}
        <button
          onClick={handlePlayToggle}
          disabled={!canPlay}
          className={`flex items-center justify-center h-8 w-8 rounded-full disabled:opacity-50 transition-colors shrink-0 ${config.buttonClassName}`}
        >
          {isLoadingUrl ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isPlayingProp ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4 ml-0.5" />
          )}
        </button>

        {/* Label and progress */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-foreground">{displayLabel}</h4>
          {(isPlayingProp || isActiveInPlayAll) && audioUrl ? (
            // Show inline progress bar when playing or when active in play-all mode
            // Keep mounted during play-all to preserve playback position
            <AudioPlayer
              src={audioUrl}
              autoPlay={isPlayingProp && !isActiveInPlayAll}
              isPlayingExternal={isActiveInPlayAll ? !isPlayAllPaused : undefined}
              onEnded={handleAudioEnded}
              onPause={() => onPlayStateChange?.(itemId, false)}
              onDurationLoaded={handleDurationLoaded}
              compact
            />
          ) : null}
        </div>

        {/* Duration - only show when not playing and not in play-all mode */}
        {!isPlayingProp && !isActiveInPlayAll && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground shrink-0">
            <Clock className="h-3 w-3" />
            {duration !== null ? formatDuration(duration) : (
              isLoadingUrl ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                '--:--'
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};
